import { useEffect, useState } from "react";
import { api } from "@/lib/api";

// Keys that mirror to the user's account (server-side) so panel/UI state persists
// across redeploys, cache clears, PWA storage buckets and devices.
const SYNC_KEYS = [
  "ud_map_height",
  "ud_map_show_chapters",
  "ud_map_view",
  "ud_directory_collapsed",
  "ud_directory_hide_deceased",
  "ud_directory_sort",
];

let loggedIn = false;
let pending = {};
let timer = null;

export function setUiLoggedIn(v) {
  loggedIn = !!v;
}

export function getUiState(key, fallback) {
  const raw = localStorage.getItem(key);
  if (raw === null) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return raw;
  }
}

function flush() {
  const patch = pending;
  pending = {};
  if (Object.keys(patch).length === 0) return;
  api.patch("/auth/ui-state", { patch }).catch(() => {});
}

export function setUiState(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* noop */
  }
  if (loggedIn && SYNC_KEYS.includes(key)) {
    pending[key] = value;
    clearTimeout(timer);
    timer = setTimeout(flush, 600);
  }
}

// Merge the account's saved UI state into localStorage (server wins), then notify hooks.
export function hydrateUiState(ui) {
  if (ui && typeof ui === "object") {
    SYNC_KEYS.forEach((k) => {
      if (k in ui && ui[k] !== null && ui[k] !== undefined) {
        try {
          localStorage.setItem(k, JSON.stringify(ui[k]));
        } catch {
          /* noop */
        }
      }
    });
  }
  window.dispatchEvent(new Event("ui-state-hydrated"));
}

// useState-like hook whose value is persisted to localStorage + the user's account.
export function useUiState(key, fallback) {
  const [val, setVal] = useState(() => getUiState(key, fallback));
  useEffect(() => {
    const h = () => setVal(getUiState(key, fallback));
    window.addEventListener("ui-state-hydrated", h);
    return () => window.removeEventListener("ui-state-hydrated", h);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  const set = (v) =>
    setVal((prev) => {
      const next = typeof v === "function" ? v(prev) : v;
      setUiState(key, next);
      return next;
    });
  return [val, set];
}
